import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track, getSlug } from '@/utils/tracker';
import { useParams } from 'react-router-dom';

const C = {
  bg: '#03050A', surface: '#0B1120', card: '#111B2E',
  border: 'rgba(255,255,255,0.07)', white: '#F2F6FA',
  muted: '#AAB8C8', fog: '#6B7E91',
  teal: '#00D4C8', gold: '#C9A84C', violet: '#7C5CFC',
  green: '#2EC8A0', amber: '#E8C547', red: '#FF3B5C',
};

const STAGES = ['Recognition', 'Gap Fill', 'Deep Dive', 'Next Steps'];
const WF_LABELS = { fractional: 'Fractional', interim: 'Interim', advisory: 'Advisory', human_strategic: 'Full-time', build_succession: 'Succession' };
const MIN_CHARS = 80;
const INFO_COLOR = '#F5C842';

function CharCount({ value }) {
  const len = (value || '').length;
  const met = len >= MIN_CHARS;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: met ? C.teal : len > MIN_CHARS * 0.7 ? C.amber : C.fog }}>
        {len} / {MIN_CHARS} min {met ? '✓' : ''}
      </span>
    </div>
  );
}

function InfoTip({ text, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [open]);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: '50%', border: `1px solid ${INFO_COLOR}80`, color: INFO_COLOR, fontSize: 9, fontWeight: 700, cursor: 'pointer', marginLeft: 6, flexShrink: 0, fontFamily: "'Inter', sans-serif", userSelect: 'none' }}>ⓘ</span>
      {open && (
        <span style={{ position: 'fixed', bottom: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(300px, 85vw)', background: C.card, border: `1px solid ${INFO_COLOR}40`, borderRadius: 12, padding: '14px 16px', zIndex: 99999, boxShadow: '0 8px 40px rgba(0,0,0,0.9)', pointerEvents: 'none', textAlign: 'left', textTransform: 'none', letterSpacing: 'normal' }}>
          {title && <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: INFO_COLOR, fontWeight: 700, marginBottom: 6, textTransform: 'none' }}>{title}</p>}
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.75, margin: 0, fontFamily: "'Inter', sans-serif", textTransform: 'none', letterSpacing: 'normal' }}>{text}</p>
        </span>
      )}
    </span>
  );
}

export default function AuditionRoom() {
  const { token } = useParams();
  const [stage, setStage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gapAnswers, setGapAnswers] = useState([]);
  const [deepDiveResponses, setDeepDiveResponses] = useState([]);
  const [nextStep, setNextStep] = useState('proceed');
  const [talentNetwork, setTalentNetwork] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [listeningIdx, setListeningIdx] = useState(null);
  const [deepIdx, setDeepIdx] = useState(0);
  const recRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'audition');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'audition', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(`${API}/api/candidate/audition/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.detail) setError(d.detail);
        else {
          setData(d);
          setGapAnswers(new Array((d.gap_questions || []).length).fill(''));
          setDeepDiveResponses(new Array((d.questions || []).length).fill(''));
        }
        setLoading(false);
      })
      .catch(() => { setError('Unable to load your Audition Room.'); setLoading(false); });
  }, [token, API]);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const hasVoice = !isIOS && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const startVoice = useCallback((section, idx) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (recRef.current) { try { recRef.current.stop(); } catch(e) {} }
    try {
      const r = new SR();
      r.continuous = false; r.interimResults = false; r.lang = navigator.language || 'en-US';
      recRef.current = r;
      setListeningIdx({ section, idx });
      r.onresult = (e) => {
        let text = '';
        for (let i = 0; i < e.results.length; i++) { if (e.results[i].isFinal) text += e.results[i][0].transcript + ' '; }
        if (!text.trim()) return;
        if (section === 'gap') setGapAnswers(prev => { const n = [...prev]; n[idx] = (n[idx] ? n[idx] + ' ' : '') + text.trim(); return n; });
        else setDeepDiveResponses(prev => { const n = [...prev]; n[idx] = (n[idx] ? n[idx] + ' ' : '') + text.trim(); return n; });
        setListeningIdx(null);
      };
      r.onerror = () => setListeningIdx(null);
      r.onend = () => setListeningIdx(null);
      r.start();
    } catch(e) { setListeningIdx(null); }
  }, []);

  const stopVoice = useCallback(() => { recRef.current?.stop(); setListeningIdx(null); }, []);
  const isListening = (section, idx) => listeningIdx?.section === section && listeningIdx?.idx === idx;

  const VoiceBtn = ({ section, idx }) => {
    const active = isListening(section, idx);
    if (isIOS) return <span style={{ fontSize: 10, color: C.fog, fontFamily: "'Overpass Mono', monospace", flexShrink: 0 }}>🎤 use keyboard mic</span>;
    if (!hasVoice) return null;
    return (
      <button onClick={() => active ? stopVoice() : startVoice(section, idx)}
        style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer', border: `1px solid ${active ? C.red : C.teal}`, background: active ? `${C.red}15` : 'none', color: active ? C.red : C.teal, fontFamily: "'Overpass Mono', monospace", flexShrink: 0, whiteSpace: 'nowrap' }}>
        {active ? '● Stop' : '🎤 Speak'}
      </button>
    );
  };

  const gapComplete = gapAnswers.length > 0 && gapAnswers.every(a => (a || '').trim().length >= MIN_CHARS);
  const deepComplete = deepDiveResponses.length > 0 && deepDiveResponses.every(r => (r || '').trim().length >= MIN_CHARS);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${API}/api/candidate/audition/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gap_responses: Object.fromEntries((data?.gap_questions || []).map((q, i) => [q, gapAnswers[i] || ''])), deep_dive_responses: deepDiveResponses, next_step: nextStep, talent_network_status: talentNetwork }),
      });
      setSubmitted(true);
    } catch { alert('Submission failed. Please try again.'); }
    setSubmitting(false);
  };

  const wfLabel = WF_LABELS[data?.workforce_type] || 'Role';

  const mono = { fontFamily: "'Overpass Mono', monospace" };
  const serif = { fontFamily: "'Instrument Serif', serif" };
  const sans = { fontFamily: "'Inter', sans-serif" };

  if (loading) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ ...mono, fontSize: 13, color: C.muted }}>Loading your Audition Room...</p>
    </div>
  );

  if (error) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>Room not found</p>
        <p style={{ color: C.muted, fontSize: 13 }}>{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${C.teal}20`, border: `2px solid ${C.teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, color: C.teal }}>✓</div>
        <p style={{ ...serif, fontSize: 28, color: C.white, marginBottom: 12 }}>Thank you, {data?.candidate_name?.split(' ')[0]}.</p>
        <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>Your responses have been received. The team at {data?.org_name} will be in touch within 48 hours.</p>
        {talentNetwork && (
          <div style={{ background: C.card, border: `1px solid ${C.teal}30`, borderRadius: 12, padding: 16 }}>
            <p style={{ ...mono, fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 6 }}>Talent Network</p>
            <p style={{ fontSize: 13, color: C.muted }}>You have joined as <strong style={{ color: C.white }}>{talentNetwork === 'actively_looking' ? '🟢 Actively Looking' : talentNetwork === 'exploring' ? '🟡 Exploring' : '🔵 Selective'}</strong>.</p>
          </div>
        )}
      </div>
    </div>
  );

  const textareaStyle = (val) => ({
    width: '100%', minHeight: 120, background: C.surface,
    border: `1px solid ${(val || '').length >= MIN_CHARS ? C.teal + '60' : C.border}`,
    borderRadius: 10, padding: 14, color: C.white, fontSize: 14,
    lineHeight: 1.7, resize: 'vertical', outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
    fontFamily: "'Inter', sans-serif",
  });

  const btnPrimary = (disabled) => ({
    flex: 3, padding: '15px', borderRadius: 12, border: 'none',
    background: disabled ? C.fog : C.teal, color: C.bg,
    fontSize: 15, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    ...sans,
  });

  const btnSecondary = {
    flex: 1, padding: '15px', borderRadius: 12,
    border: `1px solid ${C.border}`, background: 'none',
    color: C.muted, fontSize: 14, cursor: 'pointer', ...sans,
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', ...sans }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}} * { box-sizing: border-box; }`}</style>

      {/* Header */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: C.bg, zIndex: 10, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{ width: 30, height: 30, background: `${C.teal}20`, border: `1px solid ${C.teal}40`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: C.teal, fontSize: 13 }}>◈</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ ...mono, fontSize: 8, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em' }}>reAImagination · Audition Room</p>
            <p style={{ fontSize: 13, color: C.white, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data?.org_name}</p>
          </div>
        </div>
        {/* Stage dots — only show on wider screens */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {STAGES.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: i <= stage ? C.teal : C.fog, opacity: i < stage ? 0.4 : 1 }} />
              <span style={{ ...mono, fontSize: 8, color: i === stage ? C.teal : C.fog, display: 'none' }} className="stage-label">{s}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px 80px' }}>

        {/* STAGE 0: RECOGNITION */}
        {stage === 0 && (
          <div>
            <p style={{ ...mono, fontSize: 10, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
              Welcome, {data?.candidate_name?.split(' ')[0]}
            </p>
            <h1 style={{ ...serif, fontSize: 'clamp(24px, 6vw, 32px)', color: C.white, lineHeight: 1.3, marginBottom: 6 }}>
              We have been looking for someone like you.
            </h1>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 28 }}>{data?.org_name} · {wfLabel} · {data?.role_title}</p>

            <div style={{ background: `${C.teal}08`, border: `1px solid ${C.teal}25`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <p style={{ ...mono, fontSize: 9, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                What we already know about you
                <InfoTip title="Recognition stage" text="This summary was generated from publicly available professional data about you — your career history, skills, and track record. It is the starting point for a conversation, not a judgement." />
              </p>
              <p style={{ fontSize: 14, color: C.white, lineHeight: 1.85 }}>"{data?.recognition}"</p>
            </div>

            {data?.blueprint_tasks?.length > 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
                <p style={{ ...mono, fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Key tasks for this role</p>
                {data.blueprint_tasks.slice(0, 4).map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <span style={{ color: C.teal, flexShrink: 0 }}>→</span>
                    <p style={{ fontSize: 13, color: C.white, lineHeight: 1.5 }}>{t}</p>
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 28 }}>
              <p style={{ ...mono, fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>What happens next</p>
              {[
                'A few targeted questions — type, dictate, or edit',
                '3 role-specific deep-dive questions',
                'Team reviews within 48 hours',
                'You choose your next step',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${C.teal}20`, border: `1px solid ${C.teal}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: C.teal }}>{i + 1}</div>
                  <p style={{ fontSize: 13, color: C.white, lineHeight: 1.5, paddingTop: 2 }}>{s}</p>
                </div>
              ))}
            </div>

            <button onClick={() => setStage(1)} style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', background: C.teal, color: C.bg, fontSize: 15, fontWeight: 700, cursor: 'pointer', ...sans }}>Begin →</button>
            <p style={{ textAlign: 'center', fontSize: 11, color: C.fog, marginTop: 12 }}>Your data is yours. You can withdraw at any stage.</p>
          </div>
        )}

        {/* STAGE 1: GAP FILL */}
        {stage === 1 && (
          <div>
            <p style={{ ...mono, fontSize: 10, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
              Gap Fill
              <InfoTip title="What is Gap Fill?" text="These questions help the organisation understand aspects of your background that are not visible in public data. There are no right answers — just your perspective. Speak naturally or type. Each answer needs at least 80 characters." />
            </p>
            <h2 style={{ ...serif, fontSize: 'clamp(20px, 5vw, 26px)', color: C.white, lineHeight: 1.3, marginBottom: 8 }}>Help us see you more completely.</h2>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>Type, dictate, or edit your answers. Each needs at least {MIN_CHARS} characters.</p>

            {(data?.gap_questions || []).map((q, i) => (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 10, flex: 1 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${C.teal}20`, border: `1px solid ${C.teal}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: C.teal }}>{i + 1}</div>
                    <p style={{ fontSize: 14, color: C.white, lineHeight: 1.6 }}>{q}</p>
                  </div>
                  <VoiceBtn section="gap" idx={i} />
                </div>
                <textarea
                  value={gapAnswers[i] || ''}
                  onChange={e => setGapAnswers(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                  placeholder="Type / Dictate / Edit"
                  style={textareaStyle(gapAnswers[i])}
                />
                <CharCount value={gapAnswers[i] || ''} />
                {isListening('gap', i) && <p style={{ ...mono, fontSize: 10, color: C.red, marginTop: 4 }}>● Listening — speak now</p>}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={() => setStage(0)} style={btnSecondary}>← Back</button>
              <button onTouchEnd={(e) => { e.preventDefault(); if(gapComplete) setStage(2); }} onClick={() => { if(gapComplete) setStage(2); }} disabled={!gapComplete} style={btnPrimary(!gapComplete)}>Continue →</button>
            </div>
          </div>
        )}

        {/* STAGE 2: DEEP DIVE — one question at a time */}
        {stage === 2 && (() => {
          const questions = data?.questions || [];
          const total = questions.length;
          const currentQ = questions[deepIdx] || '';
          const currentAns = deepDiveResponses[deepIdx] || '';
          const currentMet = currentAns.trim().length >= MIN_CHARS;
          const allMet = deepDiveResponses.every(r => (r || '').trim().length >= MIN_CHARS);
          const isLast = deepIdx === total - 1;
          return (
            <div>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <p style={{ ...mono, fontSize: 10, color: C.violet, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Deep Dive
                  <InfoTip title="What is Deep Dive?" text="Three role-specific questions generated from the actual tasks in this role. Designed to understand how you think, not to test facts. Take your time with each one." />
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  {questions.map((_, i) => (
                    <div key={i} style={{ width: 28, height: 4, borderRadius: 2, background: i < deepIdx ? C.violet : i === deepIdx ? C.violet : C.fog, opacity: i < deepIdx ? 0.4 : 1, transition: 'all 0.3s' }} />
                  ))}
                </div>
              </div>

              {/* Question counter */}
              <p style={{ ...mono, fontSize: 10, color: C.fog, marginBottom: 12 }}>Question {deepIdx + 1} of {total}</p>

              {/* The question — large serif */}
              <div style={{ background: `${C.violet}08`, border: `1px solid ${C.violet}25`, borderRadius: 16, padding: '24px 20px', marginBottom: 24 }}>
                <p style={{ ...serif, fontSize: 'clamp(18px, 4vw, 24px)', color: C.white, lineHeight: 1.6 }}>"{currentQ}"</p>
              </div>

              {/* Answer textarea */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <p style={{ fontSize: 12, color: C.fog }}>Your answer</p>
                  <VoiceBtn section="deep" idx={deepIdx} />
                </div>
                <textarea
                  value={currentAns}
                  onChange={e => setDeepDiveResponses(prev => { const n = [...prev]; n[deepIdx] = e.target.value; return n; })}
                  placeholder="Type / Dictate / Edit"
                  style={{ ...textareaStyle(currentAns), minHeight: 160 }}
                  autoFocus
                />
                <CharCount value={currentAns} />
                {isListening('deep', deepIdx) && <p style={{ ...mono, fontSize: 10, color: C.red, marginTop: 4 }}>● Listening — speak now</p>}
              </div>

              {/* Navigation */}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  onClick={() => deepIdx === 0 ? setStage(1) : setDeepIdx(i => i - 1)}
                  style={btnSecondary}
                >← {deepIdx === 0 ? 'Back' : 'Previous'}</button>

                {isLast ? (
                  <button
                    onTouchEnd={(e) => { e.preventDefault(); if(allMet) setStage(3); }}
                    onClick={() => { if(allMet) setStage(3); }}
                    disabled={!allMet}
                    style={btnPrimary(!allMet)}
                  >Continue →</button>
                ) : (
                  <button
                    onTouchEnd={(e) => { e.preventDefault(); if(currentMet) setDeepIdx(i => i + 1); }}
                    onClick={() => { if(currentMet) setDeepIdx(i => i + 1); }}
                    disabled={!currentMet}
                    style={btnPrimary(!currentMet)}
                  >Next question →</button>
                )}
              </div>

              {/* Answered indicator */}
              {total > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
                  {questions.map((_, i) => (
                    <button key={i} onClick={() => setDeepIdx(i)} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${i === deepIdx ? C.violet : (deepDiveResponses[i]||'').trim().length >= MIN_CHARS ? C.violet+'60' : C.border}`, background: i === deepIdx ? `${C.violet}20` : 'none', color: i === deepIdx ? C.violet : (deepDiveResponses[i]||'').trim().length >= MIN_CHARS ? C.violet+'80' : C.fog, fontSize: 11, cursor: 'pointer', ...mono }}>
                      Q{i + 1} {(deepDiveResponses[i]||'').trim().length >= MIN_CHARS ? '✓' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* STAGE 3: NEXT STEPS */}
        {stage === 3 && (
          <div>
            <p style={{ ...mono, fontSize: 10, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Your Choice</p>
            <h2 style={{ ...serif, fontSize: 'clamp(20px, 5vw, 26px)', color: C.white, lineHeight: 1.3, marginBottom: 8 }}>What would you like to happen next?</h2>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>No wrong answer.</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
              {[
                { val: 'proceed', label: 'Proceed', desc: "I am interested and ready to move forward" },
                { val: 'more_info', label: 'More Info', desc: "I would like to know more before deciding" },
                { val: 'decline', label: 'Not for me', desc: "This is not the right fit at this time" },
              ].map(opt => (
                <button key={opt.val} onClick={() => setNextStep(opt.val)} style={{ padding: '14px 10px', borderRadius: 12, textAlign: 'left', border: `1px solid ${nextStep === opt.val ? C.teal : C.border}`, background: nextStep === opt.val ? `${C.teal}10` : C.card, cursor: 'pointer' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: nextStep === opt.val ? C.teal : C.white, marginBottom: 4 }}>{opt.label}</p>
                  <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{opt.desc}</p>
                </button>
              ))}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
              <p style={{ ...mono, fontSize: 10, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                Talent Network — Optional
                <InfoTip title="What is the Talent Network?" text="The reAImagination Talent Network is a private pool of verified professionals. When a relevant opportunity arises — at any organisation using the platform — we reach out directly. Your history with this specific search is never shared with any other organisation. You can leave at any time, instantly, with no questions asked." />
              </p>
              <p style={{ fontSize: 13, color: C.white, lineHeight: 1.7, marginBottom: 14 }}>Join the reAImagination Talent Network. When the right opportunity arises, we reach out directly. Your history with this search is never shared with any other organisation.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                {[
                  { val: 'actively_looking', label: '🟢 Actively Looking' },
                  { val: 'exploring', label: '🟡 Exploring' },
                  { val: 'selective', label: '🔵 Selective' },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setTalentNetwork(talentNetwork === opt.val ? null : opt.val)} style={{ padding: '10px 8px', borderRadius: 10, textAlign: 'center', fontSize: 11, border: `1px solid ${talentNetwork === opt.val ? C.gold : C.border}`, background: talentNetwork === opt.val ? `${C.gold}10` : C.surface, color: talentNetwork === opt.val ? C.gold : C.muted, cursor: 'pointer' }}>{opt.label}</button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: C.fog }}>Not joining has no effect on this process. One tap to leave, instant, no questions asked.</p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStage(2)} style={btnSecondary}>← Back</button>
              <button onClick={handleSubmit} disabled={submitting} style={{ ...btnPrimary(false), cursor: submitting ? 'wait' : 'pointer' }}>
                {submitting ? 'Submitting...' : 'Submit →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
